import React, { Fragment } from 'react'
import styles from './Panel.scss'
import { CTA } from 'common/styleguide/CTA'
import { CircleSpinner } from 'common/spinners'
import { Msg, fm, commonVerbs, commonCTAs } from 'lib/localization'
import { Set } from 'immutable'
import _ from 'lodash'
import { FormattedMessage as T, defineMessages, injectIntl } from 'react-intl'

const panelMessages = defineMessages({
  search: {
    id: 'panelMessages.search',
    defaultMessage: 'Search here ...'
  },
  untitled: {
    id: 'panelMessages.untitled',
    defaultMessage: 'Untitled'
  }
})

class _Panel extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      initialSelectedIds: props.selectedIds,
      filter: '',
      showAll: false,
      inCreateMode: false
    }
  }

  onFilterChange = (e) => {
    this.setState({ filter: e.target.value })
  }

  onOptionClicked = (e) => {
    if (e.target.type !== 'checkbox') {
      // otherwise we get two events- one for label one for input
      e.preventDefault()
    }

    if (e.target.dataset.optionId) {
      this.props.onSelectedIdsChange(parseInt(e.target.dataset.optionId, 10))
    }
  }

  onNewClicked = () => {
    this.setState({ inCreateMode: true })
  }

  onCancelClicked = () => {
    this.setState({ inCreateMode: false })
  }

  render () {
    const {
      options,
      selectedIds,
      valueAttr,
      newRecordString,
      createNewPending,
      dispatchCreate,
      showSpinner,
      collapsePanel,
      intl
    } = this.props
    const { filter, initialSelectedIds, showAll, inCreateMode } = this.state

    if (showSpinner) {
      return <CircleSpinner size={20} />
    }

    const topIds = initialSelectedIds.size > 0 && filter === ''
      ? initialSelectedIds.intersect(selectedIds)
      : Set()
    let [topOpts, rest] = _.partition(options.toJS(), o => topIds.has(o.id))
    if (filter !== '') {
      rest = rest.filter(o => o[valueAttr].match(filter))
    }
    topOpts = topOpts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    rest = rest.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

    const toShow = showAll ? topOpts.concat(rest) : topOpts.concat(rest).slice(0, 4)
    const totalLength = topOpts.length + rest.length
    const showMore = !showAll && totalLength > 4

    return (
      <div className={styles.panel}>
        {inCreateMode
          ? <CreateView
            newRecordString={newRecordString}
            onCancel={this.onCancelClicked}
            createNewPending={createNewPending}
            onSave={dispatchCreate} />
          : <Fragment>
            <div className={styles.panelUpper}>
              <input
                value={filter}
                onChange={this.onFilterChange}
                placeholder={fm(intl, panelMessages.search)}
                className={styles.panelSearch} />
              <div className={styles.panelInner}>
                {toShow.map(o => <MultiSelectRow
                  key={o.id}
                  id={o.id}
                  selected={selectedIds.has(o.id)}
                  optionClicked={this.onOptionClicked}
                  value={o[valueAttr]} />)}
                {showMore && <a
                  onClick={(e) => { this.setState({ showAll: true }) }}
                  className={styles.showMoreAnchor}>
                  <T
                    id='Panel.showMore'
                    defaultMessage='Show {count} More Results'
                    values={{ count: totalLength - 4 }} />
                </a>}
              </div>
            </div>
            <div className={styles.panelBottom}>
              <CTA style={{ margin: '0px 20px 0px 0px' }} className={styles.panelCTA} onClick={collapsePanel}>
                <Msg m={commonVerbs.save} />
              </CTA>
              <CTA link margins={false} className={styles.panelCTA} onClick={this.onNewClicked}>
                <T
                  id='Panel.newRecord'
                  defaultMessage='New {newRecordString}'
                  values={{ newRecordString }} />
              </CTA>
              {selectedIds.size > 0 && <span className={styles.selectedText}>
                <T
                  id='Panel.numSelected'
                  defaultMessage='{number} Selected'
                  values={{ number: selectedIds.size }} />
              </span>}
            </div>
          </Fragment>}
      </div>
    )
  }
}

export const Panel = injectIntl(_Panel)

class MultiSelectRow extends React.PureComponent {
  render () {
    const { id, value, selected, optionClicked } = this.props

    return (
      <div className={styles.row} onClick={optionClicked}>
        <input id={`checkboxFor${id}`} data-option-id={id} type='checkbox' checked={selected} readOnly />
        <label htmlFor={`checkboxFor${id}`} data-option-id={id}>{value}</label>
      </div>
    )
  }
}

class _CreateView extends React.PureComponent {
  state = {
    name: ''
  }

  componentDidUpdate (prevProps) {
    const { createNewPending, onCancel } = this.props

    if (prevProps.createNewPending && !createNewPending) onCancel()
  }

  onNameChange = (e) => {
    this.setState({ name: e.target.value })
  }

  onSaveClicked = (e) => {
    e.preventDefault()
    const { onSave } = this.props
    const { name } = this.state

    onSave(name)
  }

  render () {
    const { newRecordString, onCancel, createNewPending, intl } = this.props
    const { name } = this.state

    return <Fragment>
      <div className={styles.newTop}>
        <input
          value={name}
          onChange={this.onNameChange}
          disabled={createNewPending}
          placeholder={fm(intl, panelMessages.untitled)} />
        <CTA margins={false} onClick={this.onSaveClicked}><Msg m={commonVerbs.save} /></CTA>
      </div>
      <div className={styles.newInner}>
        <T
          id='SearchablePanel.createNewRecord'
          defaultMessage='Create a new {newRecordString} for your route'
          values={{ newRecordString }} />
      </div>
      <div className={styles.newBottom}>
        <CTA link margins={false} onClick={onCancel}><Msg m={commonCTAs.cancel} /></CTA>
      </div>
    </Fragment>
  }
}

const CreateView = injectIntl(_CreateView)
